/* ============================================================
   PRAXIS — <praxis-quick-add>
   Corner-plus → slide-over size panel → add to cart.
   Reuses Dawn's cart system WITHOUT editing any cart/checkout file:
   it reads window.routes, fetchConfig, publish, PUB_SUB_EVENTS (globals
   from constants.js/pubsub.js/global.js) and calls the public methods of
   the live <cart-notification>/<cart-drawer> element. Adapts automatically
   to settings.cart_type (drawer / notification / page).
   ============================================================ */
(function () {
  if (customElements.get('praxis-quick-add')) return;

  class PraxisQuickAdd extends HTMLElement {
    constructor() {
      super();
      this.plus = this.querySelector('.praxis-card__plus');
      this.panel = this.querySelector('.praxis-card__panel');
      this.addBtn = this.querySelector('.praxis-card__add');
      this.errorEl = this.querySelector('.praxis-card__error');
      this.selectedVariant = null;

      if (this.plus) {
        this.plus.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle();
        });
      }

      this.addEventListener('click', (e) => {
        const size = e.target.closest('.praxis-card__size');
        if (size && !size.hasAttribute('disabled')) {
          this.selectSize(size);
          return;
        }
        if (e.target.closest('.praxis-card__add')) {
          e.preventDefault();
          this.add();
        }
      });
    }

    /* The live cart UI element (drawer or notification), or null on cart_type 'page'. */
    get cart() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    toggle(force) {
      const open = force === undefined ? !this.classList.contains('is-open') : force;
      this.classList.toggle('is-open', open);
      if (this.plus) this.plus.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        document.querySelectorAll('praxis-quick-add.is-open').forEach((el) => {
          if (el !== this) el.close();
        });
        const firstSize = this.querySelector('.praxis-card__size:not([disabled])');
        if (firstSize) firstSize.focus();
      }
    }

    close() {
      this.toggle(false);
    }

    selectSize(btn) {
      this.querySelectorAll('.praxis-card__size').forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      this.selectedVariant = btn.dataset.variantId;
      if (this.errorEl) this.errorEl.hidden = true;
    }

    async add() {
      if (this._busy) return; // re-entrancy guard: aria-disabled does not block clicks on a button

      const sizes = this.querySelectorAll('.praxis-card__size');
      // If sizes exist but none picked yet, default to the first available one.
      if (sizes.length && !this.selectedVariant) {
        const first = this.querySelector('.praxis-card__size:not([disabled])');
        if (first) this.selectSize(first);
      }
      const variantId = this.selectedVariant || (this.addBtn && this.addBtn.dataset.defaultVariant);
      if (!variantId) return;

      const cart = this.cart;
      this._busy = true;
      this.setLoading(true);
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type']; // let FormData set its multipart boundary

        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', 1);
        if (cart) {
          body.append('sections', cart.getSectionsToRender().map((s) => s.id));
          body.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') {
            // Return focus to the corner-plus (stays visible) rather than the
            // Add button, which is hidden when the panel auto-closes.
            cart.setActiveElement(this.plus || this);
          }
        }
        config.body = body;

        const response = await fetch(window.routes.cart_add_url, config);
        const data = await response.json();

        if (data.status) {
          // 422-style error (sold out / not enough stock / invalid)
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'praxis-quick-add',
              productVariantId: variantId,
              errors: data.errors || data.description,
              message: data.message,
            });
          }
          // description can be an object keyed by field on some 422 responses.
          let msg = data.message;
          if (typeof data.description === 'string' && data.description) msg = data.description;
          this.showError(msg || (window.cartStrings && window.cartStrings.error) || 'Could not add to bag.');
          return;
        }

        if (!cart) {
          // cart_type 'page' — no UI element; mirror Dawn's redirect.
          window.location = window.routes.cart_url;
          return;
        }

        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'praxis-quick-add',
            productVariantId: variantId,
            cartData: data,
          });
        }
        cart.renderContents(data); // paints sections (incl. cart count) AND opens the drawer/notification
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        this.showAdded();
      } catch (err) {
        console.error('[praxis-quick-add]', err);
        this.showError((window.cartStrings && window.cartStrings.error) || 'Could not add to bag.');
      } finally {
        this.setLoading(false);
        this._busy = false;
      }
    }

    setLoading(on) {
      if (!this.addBtn) return;
      this.addBtn.classList.toggle('is-loading', on);
      this.addBtn.setAttribute('aria-disabled', on ? 'true' : 'false');
    }

    showError(msg) {
      if (this.errorEl) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }
    }

    showAdded() {
      if (!this.addBtn) return;
      if (!this._label) this._label = this.addBtn.textContent;
      this.addBtn.classList.add('is-added');
      this.addBtn.textContent = 'Added ✓';
      clearTimeout(this._addedTimer);
      this._addedTimer = setTimeout(() => {
        this.addBtn.classList.remove('is-added');
        this.addBtn.textContent = this._label;
        this.close();
      }, 1500);
    }
  }

  customElements.define('praxis-quick-add', PraxisQuickAdd);

  // Close any open panel on Escape or outside click.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('praxis-quick-add.is-open').forEach((el) => el.close());
    }
  });
  document.addEventListener('click', (e) => {
    if (e.target.closest('praxis-quick-add')) return;
    document.querySelectorAll('praxis-quick-add.is-open').forEach((el) => el.close());
  });
})();
